// T2 — écran « Production » (LOT 7 §1) : zone À PRÉPARER en tête (bandeau
// X commandes · Y pizzas · plus ancienne Z min, auto-entrée de toute commande
// validée, [ Commencer ] facultatif), puis EN COURS (RIEN ne s'affiche quand la
// zone est vide — contrat gate 7b), « Prêtes » en bas. L'indicateur GLOBAL
// EN AVANCE/EN RETARD est SUPPRIMÉ (7b) : les messages de délai §2 (cascade
// 4 états) sont ATTACHÉS à chaque commande via StatutDelai. Garde-fous absolus :
// zéro animation/flash/pulse, zéro son, lisible à 2 m, JAMAIS le téléphone
// (§8 RGPD). La SEULE action est « Commencer », déléguée au gate (INV-B). La
// refabrication est retirée entièrement de l'UI voie 1 (LOT 7 §5 — pizza à
// refaire = nouvelle commande). Fin de service : emplacement unique en Prise de
// commande (1 tap via la navigation permanente).
import { useState } from 'react';
import { useCatalogue } from '@/hooks/useCatalogue';
import { useMinuteTick } from '@/hooks/useMinuteTick';
import { useNumerosService } from '@/hooks/useNumerosService';
import { useT2File } from '@/hooks/useT2File';
import { useT1ActionGate } from '@/hooks/useT1ActionGate';
import { evalDelais, prochaineAPreparer } from '@/lib/heure-capacite';
import { formatHeure } from '@/lib/format';
import { pluriel } from '@/lib/pluriel';
import { nomAffichage } from '@/lib/affichage-commande';
import { CAMION_CAPACITE_CONFIG } from '@/config/camion.pizznco';
import { CarteAPreparer } from './CarteAPreparer';
import { TuileEnCours } from './TuileEnCours';
import { TuileSuivante } from './TuileSuivante';
import type { TuileT2 } from '@/hooks/useT2File';

const MS_MIN = 60_000;

function totalPizzas(tuiles: TuileT2[]): number {
  return tuiles.reduce(
    (s, t) => s + t.lignes.reduce((sl, l) => sl + l.qty, 0),
    0,
  );
}

export function T2Screen() {
  const { aPreparer, enCours, suivantes, reste, pretes, chargeCommandes } =
    useT2File();
  const catalogue = useCatalogue();
  const gate = useT1ActionGate();
  const numeros = useNumerosService();
  const nomParId = new Map(catalogue.map((p) => [p.id as number, p.nom]));

  // Messages de délai §2 attachés aux commandes (cascade 4 états, projection
  // pure sur TOUTE la file ouverte — prete/terminales sans entrée), rafraîchis
  // à la minute pleine (tick — sans navigation ni interaction).
  const now = useMinuteTick();
  const delais = evalDelais(now, chargeCommandes, CAMION_CAPACITE_CONFIG);
  const delaiDe = (t: TuileT2) => delais.get(t.commande.id as number);

  // « Commencer la suivante » (micro-lot 7d) : cible = première commande À
  // PRÉPARER selon le TRI MOTEUR (ordonnerParEcheance — source unique, aucune
  // ré-implémentation), action = la transition Commencer EXISTANTE (gate).
  const prochaineId = prochaineAPreparer(
    now,
    chargeCommandes,
    CAMION_CAPACITE_CONFIG,
  );
  const prochaine =
    prochaineId === null
      ? undefined
      : aPreparer.find((t) => t.commande.id === prochaineId);
  const prochainePizzas =
    prochaine?.lignes.reduce((s, l) => s + l.qty, 0) ?? 0;

  // VERROU anti-double-tap (complément 7d) — état transitoire UI uniquement :
  // la cible tapée est mémorisée ; le bouton est verrouillé tant que la cible
  // AFFICHÉE n'a pas changé (stabilisation liveQuery). Zéro écriture DB, zéro
  // machine d'états, zéro animation, zéro timer — pure dérivation : dès que le
  // nouveau libellé est visible, un démarrage volontaire est possible.
  const [cibleTapee, setCibleTapee] = useState<number | null>(null);
  // Échec RÉEL de démarrage (complément 2) : cible dont le démarrage a échoué —
  // l'erreur n'est visible que tant que cette cible est ENCORE l'affichée
  // (effacée au tap suivant ou au premier changement d'état stable).
  const [erreurCible, setErreurCible] = useState<number | null>(null);
  const verrouille = prochaineId !== null && prochaineId === cibleTapee;
  const erreurVisible = erreurCible !== null && erreurCible === prochaineId;

  // Rejet OBSOLÈTE attendu (rebond sur commande déjà démarrée) — forme RÉELLE
  // levée par la machinerie de transition du data layer (transactions.ts,
  // intouché) : new Error(`Transition invalide ${statut} -> ${nouveauStatut}`).
  // Match PRÉCIS côté UI — jamais de catch générique.
  const estRejetObsolete = (err: unknown): boolean =>
    err instanceof Error && err.message.startsWith('Transition invalide');

  const demarrerSuivante = (): void => {
    if (verrouille || !prochaine) return;
    const cible = prochaine.commande.id as number;
    setErreurCible(null); // effacement au tap suivant
    setCibleTapee(cible);
    gate.executer(cible, 'priorisee').catch((err: unknown) => {
      if (estRejetObsolete(err)) {
        // Rebond : la matrice W.9 a déjà protégé — absorbé, liveQuery
        // réaffiche la vraie suivante, aucune erreur visible.
        return;
      }
      // TOUTE autre erreur : réarmement immédiat (bouton réutilisable) +
      // erreur VISIBLE (rouge = action requise). Rien d'avalé.
      setCibleTapee(null);
      setErreurCible(cible);
    });
  };

  // « Tout est prêt » (micro-lot 7f) — même verrou/erreur que 7d, appliqués à
  // la commande de la TUILE : verrouillé tant qu'elle n'a pas changé ; échec
  // réel -> état atteint CONSERVÉ (non-atomique par construction), réarmement
  // immédiat + erreur visible ; « réessayer » reprend du statut persisté.
  const [toutPretTape, setToutPretTape] = useState<number | null>(null);
  const [toutPretErreur, setToutPretErreur] = useState<number | null>(null);
  const enCoursId = enCours?.commande.id as number | undefined;
  const toutPretVerrouille =
    enCoursId !== undefined && enCoursId === toutPretTape;
  const toutPretErreurVisible =
    enCoursId !== undefined && enCoursId === toutPretErreur;
  const toutEstPret = (): void => {
    if (toutPretVerrouille || !enCours) return;
    const cible = enCours.commande.id as number;
    setToutPretErreur(null); // effacement au tap suivant
    setToutPretTape(cible);
    gate
      .executerJusquaPrete(cible, enCours.commande.statut)
      .catch((err: unknown) => {
        // Réarmement dans TOUS les cas d'échec : ici la commande peut rester
        // affichée (échec intermédiaire) — un verrou dérivé seul la rendrait
        // morte. Rebond obsolète -> silencieux ; erreur réelle -> visible.
        setToutPretTape(null);
        if (!estRejetObsolete(err)) setToutPretErreur(cible);
      });
  };

  // Bandeau À PRÉPARER : X commandes · Y pizzas · plus ancienne Z min.
  const plusAncienneMin =
    aPreparer.length > 0
      ? Math.max(
          0,
          Math.floor(
            (now - Math.min(...aPreparer.map((t) => t.commande.createdAt))) /
              MS_MIN,
          ),
        )
      : 0;

  return (
    <section aria-labelledby="t2-title" className="flex flex-col gap-4 pb-6">
      <h1 id="t2-title" className="px-4 pt-4 font-jost text-3xl font-bold">
        Production
      </h1>

      {/* Zone À PRÉPARER en tête — toute commande validée entre ici
          automatiquement ; rien ne disparaît sans « Commencer ». */}
      <section aria-label="À préparer" className="flex flex-col gap-3 px-4">
        <h2 className="font-jost text-2xl font-bold">À préparer</h2>
        {aPreparer.length === 0 ? (
          <p className="rounded-lg border border-ardoise/15 bg-card/40 px-4 py-6 text-center font-jost text-xl text-ardoise/60">
            Aucune commande à préparer
          </p>
        ) : (
          <>
            <p
              data-testid="bandeau-a-preparer"
              className="rounded-lg bg-ardoise px-4 py-3 font-jost text-xl text-creme"
            >
              <span className="font-mono">{aPreparer.length}</span>{' '}
              {pluriel(aPreparer.length, 'commande')} ·{' '}
              <span className="font-mono">{totalPizzas(aPreparer)}</span>{' '}
              {pluriel(totalPizzas(aPreparer), 'pizza')} · plus ancienne{' '}
              <span className="font-mono">{plusAncienneMin}</span> min
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {aPreparer.map((t) => (
                <CarteAPreparer
                  key={t.commande.id}
                  tuile={t}
                  nomParId={nomParId}
                  numeros={numeros}
                  delai={delaiDe(t)}
                  onCommencer={(id) => void gate.executer(id, 'priorisee')}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {/* « Commencer la suivante » (7d) — élément AUTONOME en tête de la zone
          EN COURS, affiché ssi ≥ 1 commande À PRÉPARER, y compris quand EN
          COURS est vide (règle 7b « vide -> rien » AMENDÉE : ce bouton est le
          SEUL élément autorisé à vide). Libellé explicite — pas de démarrage
          surprise. Bouton MAISON, zéro animation. */}
      {prochaine && (
        <div className="px-4">
          <button
            type="button"
            data-testid="commencer-suivante"
            disabled={verrouille}
            onClick={demarrerSuivante}
            className="min-h-[96px] w-full rounded-lg bg-ardoise font-jost text-2xl text-creme"
          >
            Commencer · {nomAffichage(prochaine.commande, numeros)} ·{' '}
            {prochainePizzas} {pluriel(prochainePizzas, 'pizza')}
          </button>
          {erreurVisible && (
            <p
              data-testid="erreur-demarrage"
              className="mt-2 font-jost text-lg font-bold text-destructive"
            >
              Échec du démarrage — réessayer
            </p>
          )}
        </div>
      )}

      {/* Zone EN COURS — RIEN d'autre ne s'affiche lorsqu'elle est vide
          (gate 7b, amendé 7d : seul le bouton ci-dessus est autorisé). */}
      {enCours && (
        <section aria-label="En cours" className="flex flex-col gap-3">
          <h2 className="px-4 font-jost text-2xl font-bold">En cours</h2>
          <TuileEnCours
            tuile={enCours}
            nomParId={nomParId}
            numeros={numeros}
            delai={delaiDe(enCours)}
            onToutPret={toutEstPret}
            toutPretVerrouille={toutPretVerrouille}
            toutPretErreur={toutPretErreurVisible}
          />
          {suivantes.length > 0 && (
            <div className="grid grid-cols-1 gap-3 px-4 sm:grid-cols-2">
              {suivantes.map((t, i) => (
                <TuileSuivante
                  key={t.commande.id}
                  tuile={t}
                  nomParId={nomParId}
                  rang={i + 1}
                  delai={delaiDe(t)}
                />
              ))}
            </div>
          )}
          {reste > 0 && (
            <p
              data-testid="reste-attente"
              className="px-4 pb-3 font-jost text-muted-foreground"
            >
              <span className="font-mono">+{reste}</span> en attente
            </p>
          )}
        </section>
      )}

      {/* Prêtes en bas — plus de message de délai (cascade règle 0). */}
      <section aria-label="Prêtes" className="flex flex-col gap-3 px-4">
        <h2 className="font-jost text-2xl font-bold">Prêtes</h2>
        {pretes.length === 0 ? (
          <p className="rounded-lg border border-ardoise/15 bg-card/40 px-4 py-6 text-center font-jost text-xl text-ardoise/60">
            Aucune commande prête
          </p>
        ) : (
          pretes.map((t) => (
            <div
              key={t.commande.id}
              data-testid="carte-prete"
              className="flex items-baseline justify-between gap-3 rounded-lg bg-card/60 p-4"
            >
              <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span
                  className={
                    t.commande.client_nom_snapshot
                      ? 'font-cormorant text-3xl italic'
                      : 'font-jost text-2xl font-medium'
                  }
                >
                  {nomAffichage(t.commande, numeros)}
                </span>
                <span className="font-jost text-2xl">
                  · {t.lignes.reduce((s, l) => s + l.qty, 0)}{' '}
                  {pluriel(
                    t.lignes.reduce((s, l) => s + l.qty, 0),
                    'pizza',
                  )}
                </span>
              </span>
              {/* « ça attend depuis quand » : instant de PASSAGE EN PRÊTE
                  (updatedAt — proxy VOIE 1, même consigne que 7g : écrasé par
                  l'écrivain unique à chaque transition, fiable car aucune
                  écriture sur une commande restée prête ; la voie 2 basculera
                  sur son horodatage explicite — NE PAS généraliser). */}
              <span className="font-jost text-2xl text-muted-foreground">
                prête à{' '}
                <span
                  className="font-mono text-ardoise"
                  data-testid="prete-depuis"
                >
                  {formatHeure(t.commande.updatedAt)}
                </span>
              </span>
            </div>
          ))
        )}
      </section>
    </section>
  );
}
